import React, { useEffect, useState } from 'react';

import routePaths from 'config/routes';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { getDataCall } from 'services/api';
import apiPaths from 'services/apiPaths';
import { privateRoute } from 'services/privateRoute';
import { setBreadCrumbs, updateChallenges } from 'src/actions/appActions';
import { getBreadCrumbs, NotFound } from 'src/components';
import { builderChallenge } from 'src/components/challenge/builder/BuilderChallenge';
import BuildPageChallenge, {
  showMoreButton,
} from 'src/components/challenge/ChallengeBuildPage';
import ChallengeCard from 'src/components/challenge/ChallengeCard';
import ChallengeFilter from 'src/components/challenge/ChallengeFilter';
import { IRootReducers } from 'src/reducers';
import {
  ChallengedTabsEnum,
  ChallengeTypeEnum,
  PlatformEnums,
} from 'src/shared/enums';
import {
  ChallengeCardButton,
  ChallengeDetail,
  IChallenge,
} from 'src/shared/models';
import { appTypes } from 'src/types/appTypes';
import {
  fetchInitialData,
  getTranslatedData,
  renderChallengeCard,
} from 'utils';
import { ReduxPageContext } from 'src/store';

const { GET_CAMPAIGNS } = apiPaths.CHALLENGES;

interface OwnProps {
  children?: any;
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

export const renderChallengeCampaign = (
  challenge: ChallengeDetail
): JSX.Element => {
  const { formatMessage } = useIntl();

  let config: ChallengeDetail;
  let userStatus: { idResource: number; status: number };
  const challengeCardButton: ChallengeCardButton = {
    title: formatMessage({ id: 'talk.see' }),
    path: routePaths.PAGES.CAMPAIGN_DETAIL,
    className: 'challengeCard__link challengeCard__link--active',
  };

  config = {
    ...challenge,
    challengeName: getTranslatedData(challenge, 'name'),
    challengeDescription:
      getTranslatedData(challenge, 'description') ||
      getTranslatedData(challenge, 'descriptionLong'),
    challengeCardButton,
    userStatus,
  };

  return <ChallengeCard {...config} />;
};

const CampaignsPage: NextPage<Props> = ({
  campaignsList,
  setBreadCrumbs,
  updateChallenges,
  user,
}): JSX.Element => {
  const { formatMessage } = useIntl();
  const router = useRouter();

  const [campaigns, setCampaigns] = useState<ChallengeDetail[]>();
  const [filters, setFilters] = useState<ChallengedTabsEnum>(
    ChallengedTabsEnum.ALL
  );

  const [canShowMore, setCanShowMore] = useState<boolean>(
    campaignsList.length >= 6
  );
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [requestPage, setRequestPage] = useState<number>(2); //Hemos pedido 8 y las páginas van de 4 en 4. Así que toca la pagina 2

  const handleShowMore = async () => {
    setIsLoadingMore(true);

    const _categories = '';

    // TODO: categories are not sended correctly to the end point.
    // if (requestCategories.length > 0) {
    //   _categories = `&categories=${requestCategories.join()}`;
    // }

    const request = `${GET_CAMPAIGNS}?page=${requestPage}&size=3${_categories}`;

    // TODO: Estas líneas se repiten en las otras challenge-lists. Centralizar en challenge/utils.
    try {
      const response = await getDataCall({
        dataPath: request,
        callConfig: {},
      });

      const ids = new Set(campaigns.map((campaign) => campaign.challengeId));
      const newchallengeAcc: ChallengeDetail[] = [
        ...campaignsList,
        ...response.data
          .filter((campaign: IChallenge) => !ids.has(campaign.idChallenge))
          .map((campaign: IChallenge) => builderChallenge(campaign, user)),
      ];

      updateChallenges({ campaigns: newchallengeAcc });

      setRequestPage(requestPage + 1);
      setCanShowMore(response.data.length == 3);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const renderCampaigns = (): JSX.Element | JSX.Element[] => {
    let filteredCampaigns: ChallengeDetail[] = [];

    if (!campaigns?.length) return <NotFound />;

    switch (filters) {
      case ChallengedTabsEnum.PENDING:
        filteredCampaigns = campaigns.filter(
          ({ isChallengeCompleted, isCampaignActive }) =>
            !isChallengeCompleted && isCampaignActive
        );
        break;
      case ChallengedTabsEnum.COMPLETED:
        filteredCampaigns = campaigns.filter(
          ({ isChallengeCompleted }) => isChallengeCompleted
        );
        break;
      case ChallengedTabsEnum.FAVORITES:
        filteredCampaigns = campaigns.filter(
          ({ challengeLikes: { liked } }) => liked
        );
        break;
      default:
        filteredCampaigns = campaigns;
        break;
    }

    if (!filteredCampaigns.length) return <NotFound />;

    return filteredCampaigns.map((challenge: ChallengeDetail) =>
      renderChallengeCard({ challenge })
    );
  };

  useEffect(() => {
    const breadCrumbs = getBreadCrumbs(router, formatMessage);
    setTimeout(() => {
      setBreadCrumbs(breadCrumbs);
    }, 20);

    // clear breadcrumbs on unmount component
    return () => {
      // TODO: Remove setTimeout => check store.ts
      setTimeout(() => {
        setBreadCrumbs([]);
      }, 20);
    };
  }, []);

  const filterComponent: JSX.Element = (
    <ChallengeFilter
      challengeType={ChallengeTypeEnum.CAMPAIGN}
      materialTypes={[]}
      setChallengeList={setCampaigns}
      challengeList={campaignsList}
    />
  );

  const showMoreComponent =
    filters == ChallengedTabsEnum.ALL
      ? showMoreButton(
          `page.marketing-materials.show-more`,
          isLoadingMore,
          canShowMore,
          handleShowMore
        )
      : null;

  return (
    <BuildPageChallenge
      pageClassName="campaignsPage"
      onChangeTab={setFilters}
      renderChallenges={renderCampaigns}
      showMoreComponent={showMoreComponent}
      filterComponent={filterComponent}
      tabs={[
        { key: ChallengedTabsEnum.ALL, label: 'app.all' },
        { key: ChallengedTabsEnum.PENDING, label: 'app.pending' },
        { key: ChallengedTabsEnum.COMPLETED, label: 'app.completed' },
        { key: ChallengedTabsEnum.FAVORITES, label: 'app.my-favourites' },
      ]}
    />
  );
};

/*-------------------------------------------*/
/*------------ GET INITIAL PROPS ------------*/
/*-------------------------------------------*/

const initialAction = async (
  accessToken: string,
  ctx: ReduxPageContext
): Promise<{ data: unknown }> => {
  try {
    // TODO:Move 6 to config/config.ts
    const challenges = await fetchInitialData(
      accessToken,
      `${GET_CAMPAIGNS}?size=6`,
      ctx
    );

    const campaigns: ChallengeDetail[] = challenges.map(
      (challenge: IChallenge) =>
        builderChallenge(challenge, ctx.store.getState().auth.user)
    );

    // TODO: APPLY this to no re-request [or reload] challenges. Same to TALKS and TRAININGS
    // const saveChallenges: ChallengeDetail[] = ctx.store.getState().app
    //   .challenges.campaigns;
    // const newsChallengesID: number[] = campaigns.map(
    //   ({ challengeId }) => challengeId,
    // );

    // if (campaigns)
    //   ctx.store.dispatch({
    //     type: appTypes.APP_UPDATE_CHALLENGES,
    //     payload: {
    //       campaigns: [
    //         ...saveChallenges.filter(
    //           ({ challengeId }) => !newsChallengesID.includes(challengeId),
    //         ),
    //         ...campaigns,
    //       ],
    //     },
    //   });

    if (campaigns)
      ctx.store.dispatch({
        type: appTypes.APP_UPDATE_CHALLENGES,
        payload: { campaigns },
      });

    return { data: [] };
  } catch (error) {
    console.error('SERVER ERROR: ', error);
    return { data: null };
  }
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
    campaignsList: state.app.challenges.campaigns,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ updateChallenges, setBreadCrumbs }, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  privateRoute({
    WrappedComponent: CampaignsPage,
    initialAction,
    platforms: [PlatformEnums.PIN],
  })
);
